import type React from 'react';
import { Microscope, Sigma, Brain, Landmark, Palette, Cpu, Scale } from 'lucide-react';
import type { IntellectualSphereName } from './intellectualSpheres';

/**
 * The 7 **Universal Knowledge Paths** (INTELLECTUAL_DOMAIN_SPEC §2) — the knowledge
 * map's top tier, REPLACING the old academic-field "spheres". Each Path is a class
 * of knowledge (a crest + a core question + a curated catalog of example Subjects).
 * Subjects (= the trainable unit, an Occupational "Role" with primary+secondary Path
 * + the 11-rank Uninitiated→Luminary ladder) belong to a Path; a Path's level is the
 * aggregate of its Subjects.
 *
 * IN0 ships this as a pure constant (no migration). Old sphere-tagged data is mapped
 * on read via `resolveIntellectualPath`; the DB CHECK swap lands with the new Subject
 * picker (IN2).
 */
export type IntellectualPathName =
  | 'Natural Sciences'
  | 'Formal Sciences'
  | 'Human Sciences'
  | 'Civilizational Studies'
  | 'Creative Arts & Design'
  | 'Technology & Applied Systems'
  | 'Philosophy & Wisdom Traditions';

export interface IntellectualPath {
  name: IntellectualPathName;
  /** A single-word label for tight UI (the Areas rail list), so the name never truncates. */
  shortName: string;
  /** The path's guiding question. */
  coreQuestion: string;
  description: string;
  /** Crest icon (Lucide component). */
  icon: React.ElementType;
  /** Crest color / path accent. */
  color: string;
  /** Curated example Subjects — the catalog options for the Subject picker (+ custom). */
  exampleSubjects: string[];
}

export const INTELLECTUAL_PATHS: Record<IntellectualPathName, IntellectualPath> = {
  'Natural Sciences': {
    name: 'Natural Sciences',
    shortName: 'Nature',
    coreQuestion: 'How does nature work?',
    description: 'Understanding the physical and living world — matter, energy, life, and the systems of the Earth and cosmos.',
    icon: Microscope,
    color: '#22c55e',
    exampleSubjects: ['Physics', 'Chemistry', 'Biology', 'Earth Science', 'Astronomy', 'Medicine & Health Science', 'Environmental Science'],
  },
  'Formal Sciences': {
    name: 'Formal Sciences',
    shortName: 'Logic',
    coreQuestion: 'How do patterns, logic, and systems work?',
    description: 'Understanding abstract systems — quantity, logic, computation, and pattern.',
    icon: Sigma,
    color: '#3b82f6',
    exampleSubjects: ['Mathematics', 'Logic', 'Statistics', 'Computer Science', 'Systems Theory', 'Information Theory', 'Decision Science'],
  },
  'Human Sciences': {
    name: 'Human Sciences',
    shortName: 'Human',
    coreQuestion: 'How do humans behave and develop?',
    description: 'Understanding people — behavior, development, identity, language, and groups.',
    icon: Brain,
    color: '#f97316',
    exampleSubjects: ['Psychology', 'Sociology', 'Anthropology', 'Education', 'Communication Studies', 'Linguistics', 'Human Development'],
  },
  'Civilizational Studies': {
    name: 'Civilizational Studies',
    shortName: 'Society',
    coreQuestion: 'How do societies, cultures, and history evolve?',
    description: 'Understanding societies — institutions, history, governance, economics, and collective life.',
    icon: Landmark,
    color: '#eab308',
    exampleSubjects: ['History', 'Politics & Governance', 'Economics', 'Law', 'Geography', 'International Relations', 'Cultural Studies'],
  },
  'Creative Arts & Design': {
    name: 'Creative Arts & Design',
    shortName: 'Arts',
    coreQuestion: 'How do humans create beauty, meaning, and expression?',
    description: 'Understanding expression — aesthetics, creativity, and symbolic making.',
    icon: Palette,
    color: '#a855f7',
    exampleSubjects: ['Visual Arts', 'Music', 'Literature & Writing', 'Design', 'Architecture & Spatial Design', 'Film & Media', 'Performance Arts'],
  },
  'Technology & Applied Systems': {
    name: 'Technology & Applied Systems',
    shortName: 'Technology',
    coreQuestion: 'How do we build tools, machines, and applied solutions?',
    description: 'Understanding how knowledge becomes tools, products, infrastructure, and practical systems.',
    icon: Cpu,
    color: '#06b6d4',
    exampleSubjects: ['Engineering', 'Software & Web Development', 'Artificial Intelligence', 'Business & Management Systems', 'Medical & Health Technology', 'Manufacturing & Production', 'Urban & Infrastructure Systems'],
  },
  'Philosophy & Wisdom Traditions': {
    name: 'Philosophy & Wisdom Traditions',
    shortName: 'Wisdom',
    coreQuestion: 'What is true, good, meaningful, and worth pursuing?',
    description: 'Understanding meaning — truth, ethics, reality, the self, and ultimate questions.',
    icon: Scale,
    color: '#ef4444',
    exampleSubjects: ['Philosophy', 'Ethics', 'Religion & Theology', 'Spirituality', 'Comparative Wisdom Traditions', 'Existential Studies', 'Aesthetics & Meaning'],
  },
};

export const INTELLECTUAL_PATH_NAMES: IntellectualPathName[] = [
  'Natural Sciences',
  'Formal Sciences',
  'Human Sciences',
  'Civilizational Studies',
  'Creative Arts & Design',
  'Technology & Applied Systems',
  'Philosophy & Wisdom Traditions',
];

/**
 * Legacy academic-field "sphere" → Knowledge Path (IN0). Maps the old seeded
 * spheres + any pre-swap subject/resource tags onto the new universal Paths.
 * (Wellbeing & Ecology both land in Natural Sciences — a lossy but harmless remap;
 * the dogfood had no real subjects.)
 */
export const LEGACY_SPHERE_TO_PATH: Record<IntellectualSphereName, IntellectualPathName> = {
  Wellbeing: 'Natural Sciences',
  Society: 'Human Sciences',
  Commerce: 'Civilizational Studies',
  Ecology: 'Natural Sciences',
  Innovation: 'Technology & Applied Systems',
  Creativity: 'Creative Arts & Design',
  Justice: 'Philosophy & Wisdom Traditions',
};

/**
 * Normalize a stored value to a Knowledge Path: pass-through for a path name, map
 * legacy sphere names, default anything unknown/missing to 'Natural Sciences' so the
 * UI never reads an undefined crest (whether or not the IN2 migration has run).
 */
export function resolveIntellectualPath(value: string | null | undefined): IntellectualPathName {
  if (value && value in INTELLECTUAL_PATHS) return value as IntellectualPathName;
  if (value && value in LEGACY_SPHERE_TO_PATH) return LEGACY_SPHERE_TO_PATH[value as IntellectualSphereName];
  return 'Natural Sciences';
}

export const getIntellectualPath = (name: IntellectualPathName): IntellectualPath =>
  INTELLECTUAL_PATHS[name] ?? INTELLECTUAL_PATHS['Natural Sciences'];
